import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Organizer } from '../../shared/models/Organizer';
import { environment } from '../../../environments/environment';
import { OrganizerFilterOptions, SwitchOrganizer } from '../../shared/models/SwitchOrganizer';
import { PagingOffset } from '../../shared/models/Paging';
import { ViewsIntegrationData } from '../../shared/models/ViewsIntegration';
import { Observable, firstValueFrom, tap, of } from 'rxjs';
import { BookingPageTemplate } from '@deltager/organizers/admin/modules/booking-event-setup/models/page-template.model';
import { Event } from '@deltager/organizers/admin/modules/events/create-event/model/event.model';
import { FotoWebSettings } from '../../shared/models/FotoWebSettings';
import { Store } from '@ngrx/store';
import { RootState, SetOrganizerAction } from '../../redux/root.state';
import { MeetingDetails } from '../../modules/booking-event-setup/models/meeting-details.model';
import { PagedList } from '../../shared/models/paged-list.model';
import { OrganizerSettings } from '@deltager/organizers/admin/shared/models/OrganizerSettings';
import { SessionCacheService } from './session-cache.service';
import { ImageSourceSettings } from '../../shared/models/ImageSourceSettings';
import { PapirflySettings } from '../../shared/models/PapirflySettings';

@Injectable({
  providedIn: 'root',
})
export class OrganizersService {
  private API_ENDPOINT = `${environment.apiBaseURL}/organizers`;

  constructor(
    private http: HttpClient,
    private store: Store<RootState>,
    private cacheService: SessionCacheService,
  ) {}

  public getBookingPageTemplates(organizerId: number): Observable<BookingPageTemplate[]> {
    return this.http.get<BookingPageTemplate[]>(
      `${environment.apiBaseURL}/organizers/${organizerId}/booking-page-templates`,
    );
  }

  getOrganizers(filter: OrganizerFilterOptions, paging: PagingOffset): Promise<PagedList<SwitchOrganizer>> {
    const queryParams = new HttpParams()
      .set('filterType', filter.searchType.toString())
      .set('query', filter.searchQuery)
      .set('fetchAll', filter.fetchAll.toString())
      .set('offset', paging.offset.toString())
      .set('limit', paging.limit.toString());

    return firstValueFrom(this.http.get<PagedList<SwitchOrganizer>>(this.API_ENDPOINT, { params: queryParams }));
  }

  getOrganizerById(organizerId: number): Observable<Organizer> {
    return this.http.get<Organizer>(`${this.API_ENDPOINT}/${organizerId}`);
  }

  refetchOrganizerSettings(organizerId: number) {
    this.clearOrganizerSettingsCache(organizerId);
    this.getOrganizerSettingsById(organizerId).subscribe();
  }

  getOrganizerSettingsById(organizerId: number): Observable<OrganizerSettings> {
    const cacheKey = `organizerSettings_${organizerId}`;

    // Check if we have valid cached data
    const cachedData = this.cacheService.get<OrganizerSettings>(cacheKey);
    if (cachedData) {
      // Return cached data as an Observable
      this.store.dispatch(new SetOrganizerAction(cachedData));
      return of(cachedData);
    }

    // No valid cache, fetch from API
    return this.http.get<OrganizerSettings>(`${this.API_ENDPOINT}/${organizerId}/settings`).pipe(
      tap(organizer => {
        // Cache the result with expiration
        this.cacheService.set(cacheKey, organizer);

        // Update store
        this.store.dispatch(new SetOrganizerAction(organizer));
      }),
    );
  }

  /**
   * Clear cache for a specific organizer or all organizers
   */
  clearOrganizerSettingsCache(organizerId?: number): void {
    if (organizerId !== undefined) {
      // Clear specific organizer cache
      this.cacheService.remove(`organizerSettings_${organizerId}`);
    } else {
      // Clear all organizer caches
      this.cacheService.clear('organizerSettings_');
    }
  }

  createOrganizer(organizerData: Organizer): Promise<Organizer> {
    return this.http.post<Organizer>(this.API_ENDPOINT, organizerData).toPromise();
  }

  async updateOrganizer(organizerData: Organizer): Promise<Organizer> {
    const result = await firstValueFrom(this.http.put<Organizer>(`${this.API_ENDPOINT}`, organizerData));
    this.clearOrganizerSettingsCache();

    const settings = await firstValueFrom(this.getOrganizerSettingsById(result.id));
    const action = new SetOrganizerAction(settings);
    this.store.dispatch(action);

    return result;
  }

  getOrganizeViewsIntegration(organizerId: number): Promise<ViewsIntegrationData> {
    return this.http.get<ViewsIntegrationData>(`${this.API_ENDPOINT}/${organizerId}/views_integration`).toPromise();
  }

  async getImageSourceSettings(organizerId: number): Promise<ImageSourceSettings> {
    const s = await firstValueFrom(
      this.http.get<ImageSourceSettings>(`${this.API_ENDPOINT}/${organizerId}/imagesource-settings`),
    );

    if (!s) {
      return null;
    }

    const imageSourceSettings = new ImageSourceSettings(organizerId);

    let fotoWebSettings: FotoWebSettings;

    if (s?.fotoWeb) {
      fotoWebSettings = new FotoWebSettings(
        s.fotoWeb?.id,
        s.fotoWeb?.clientId,
        s.fotoWeb?.clientSecret,
        s.fotoWeb?.server,
        s.fotoWeb?.isClientSecretSet,
        s.fotoWeb?.copiedFromAffiliateLevel,
      );
    }

    let papirflySettings: PapirflySettings;

    if (s?.papirfly) {
      papirflySettings = new PapirflySettings(
        s.papirfly?.id,
        s.papirfly?.server,
        s.papirfly?.companyId,
        s.papirfly?.archiveSelector,
        s.papirfly?.languageId,
        s.papirfly?.defaultServer,
        s.papirfly?.defaultLanguageId,
      );
    }

    imageSourceSettings.fotoWeb = fotoWebSettings;
    imageSourceSettings.papirfly = papirflySettings;

    return imageSourceSettings;
  }

  async getFotoWebSettings(organizerId: number): Promise<FotoWebSettings> {
    const s = await firstValueFrom(
      this.http.get<FotoWebSettings>(`${this.API_ENDPOINT}/${organizerId}/fotoweb-settings`),
    );

    return new FotoWebSettings(
      s?.id,
      s?.clientId,
      s?.clientSecret,
      s?.server,
      s?.isClientSecretSet,
      s?.copiedFromAffiliateLevel,
    );
  }

  updateViewsIntegration(viewsData: ViewsIntegrationData): Promise<boolean> {
    return this.http.put<boolean>(`${this.API_ENDPOINT}/${viewsData.clubid}/views_integration`, viewsData).toPromise();
  }

  saveFotoWebSettings(organizerId: number, settings: FotoWebSettings): Promise<FotoWebSettings> {
    return firstValueFrom(
      this.http.put<FotoWebSettings>(`${this.API_ENDPOINT}/${organizerId}/fotoweb-settings`, settings),
    );
  }

  async savePapirflySettings(organizerId: number, settings: PapirflySettings): Promise<PapirflySettings> {
    const result = await firstValueFrom(
      this.http.put<PapirflySettings>(`${this.API_ENDPOINT}/${organizerId}/papirfly-settings`, settings),
    );

    return new PapirflySettings(
      result.id,
      result.server,
      result.companyId,
      result.archiveSelector,
      result.languageId,
      result.defaultServer,
      result.defaultLanguageId,
    );
  }

  getEventById(organizerId: number, eventId: number): Observable<Event> {
    return this.http.get<Event>(`${this.API_ENDPOINT}/${organizerId}/events/${eventId}`);
  }

  getMeetingDetails(organizerId: number, userAssetId: number): Promise<MeetingDetails> {
    return firstValueFrom(
      this.http.get<MeetingDetails>(`${this.API_ENDPOINT}/${organizerId}/user-asset/${userAssetId}/meeting-details`),
    );
  }

  updateMeetingDetails(organizerId: number, userAssetId: number, meetingDetails: MeetingDetails) {
    return firstValueFrom(
      this.http.put<MeetingDetails>(
        `${this.API_ENDPOINT}/${organizerId}/user-asset/${userAssetId}/meeting-details`,
        meetingDetails,
      ),
    );
  }
}
